import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Flex, Grid, HStack, Select, Spacer, Text, useToast } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { SingleDatepicker } from '../../single'
import { getErrorMessage } from '../../utils/utils'
import { log } from '../../utils/logger'
import { createExportApi } from '../../utils/api-interface'
import { json2csv } from 'json-2-csv'
import { DashboardProfile } from '../../services/network'
import { useSelector } from 'react-redux'
import { RootState } from '../../store/store'

type Interval = 'last_day' | 'last_week' | 'last_month' | 'last_year' | 'custom'

function GraphDashboard(): JSX.Element {
  const { t } = useTranslation()
  const [from, setFrom] = useState<number>(dayjs().unix() * 1000)
  const [to, setTo] = useState<number>(dayjs().unix() * 1000)
  const [intervalType, setIntervalType] = useState<Interval>('last_year')
  const [lastUpdate, setLastUpdate] = useState<string | undefined>(undefined)
  const [loading, isLoading] = useState<boolean>(false)
  const toast = useToast()

  const dashboardUser: DashboardProfile | null = useSelector((state: RootState) => state.dashboardUser.dashboardUser)
  const canUpdate = useMemo(() => {
    if (!dashboardUser) {
      return false
    }

    if (!dashboardUser.role || !dashboardUser.role.title) {
      return false
    }

    return dashboardUser.role.title === process.env.REACT_APP_ROLE_ADMINISTRATOR || dashboardUser.role.title === process.env.REACT_APP_ROLE_BACKOFFICE
  }, [dashboardUser])

  const setIntervalPeriod = useCallback(() => {
    const hour = 60 * 60 * 1000
    const day = 24 * hour
    const week = 7 * day
    const month = 30 * day
    const year = 365 * day
    const now = dayjs().unix() * 1000
    let then = now
    switch (intervalType) {
      case 'last_day':
        then = now - day
        break
      case 'last_week':
        then = now - week
        break
      case 'last_month':
        then = now - month
        break
      case 'last_year':
        then = now - year
        break
      default:
        return
    }

    setLastUpdate(dayjs(now).format(`DD/MM/YYYY HH:mm:ss`))
    setFrom(then)
    setTo(now)
  }, [intervalType])

  const refreshData = useCallback(() => {
    if (loading) {
      return
    }
    isLoading(true)
    setIntervalPeriod()
    setTimeout(() => isLoading(false), 5 * 1000)
  }, [loading, setIntervalPeriod])

  useEffect(() => setIntervalPeriod(), [intervalType, setIntervalPeriod])

  const fromDate = useCallback(
    (date: Date) => {
      const millis = date.getTime()
      // the selected `from` is after the current `to`
      if (millis >= to) {
        toast({
          title: t('from_error'),
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        return
      }

      if (millis > Date.now()) {
        toast({
          title: t('future_error'),
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        return
      }

      setFrom(millis)
    },
    [to, t, toast]
  )

  const toDate = useCallback(
    (date: Date) => {
      const millis = date.getTime()
      // the selected `to` is before the current `to`
      if (millis < from) {
        toast({
          title: t('past_error'),
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        return
      }
      if (millis > Date.now()) {
        toast({
          title: t('future_error'),
          status: 'error',
          duration: 5000,
          isClosable: true
        })
        return
      }

      setTo(millis)
    },
    [from, t, toast]
  )

  const generateUsersCsv = useCallback(async () => {
    if (!from || !to || !canUpdate) {
      return
    }

    try {
      const jsonData = await createExportApi().exportUsers(dayjs(from).format(`YYYY-MM-DD`), dayjs(to).format(`YYYY-MM-DD`))
      log.debug(JSON.stringify(jsonData.data, null, ' '))
      // Converti i dati JSON in CSV
      const csvContent = await json2csv(
        jsonData.data.map((v) => ({
          time: dayjs(v.time).format(`YYYY-MM-DD`),
          usersActive: v.usersActive,
          usersTotal: v.usersTotal
        }))
      )
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)

      // Crea e attiva il download
      const a = document.createElement('a')
      a.href = url
      a.download = `${dayjs().format('YYYYMMDD')}_users_stats.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast({
        title: t('csv_success'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error) {
      log.error('Errore nella conversione JSON a CSV', error)
      toast({
        title: t('csv_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  }, [from, to, canUpdate, toast, t])

  const generatePaymentsCsv = useCallback(async () => {
    if (!from || !to || !canUpdate) {
      return
    }

    try {
      const jsonData = await createExportApi().exportPayments(dayjs(from).format(`YYYY-MM-DD`), dayjs(to).format(`YYYY-MM-DD`))
      log.debug(JSON.stringify(jsonData.data, null, ' '))
      // Converti i dati JSON in CSV
      const csvContent = await json2csv(
        jsonData.data.map((v) => ({
          time: dayjs(v.time).format(`YYYY-MM-DD`),
          status: v.status
        }))
      )
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)

      // Crea e attiva il download
      const a = document.createElement('a')
      a.href = url
      a.download = `${dayjs().format('YYYYMMDD')}_payments_stats.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast({
        title: t('csv_success'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error) {
      log.error('Errore nella conversione JSON a CSV', error)
      toast({
        title: t('csv_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  }, [from, to, canUpdate, toast, t])

  const generateEventsCsv = useCallback(async () => {
    if (!from || !to || !canUpdate) {
      return
    }
    try {
      const jsonData = await createExportApi().exportEvents(dayjs(from).format(`YYYY-MM-DD`), dayjs(to).format(`YYYY-MM-DD`))
      log.debug(JSON.stringify(jsonData.data, null, ' '))
      // Converti i dati JSON in CSV
      const csvContent = await json2csv(
        jsonData.data.map((v) => ({
          time: dayjs(v.time).format(`YYYY-MM-DD`),
          isFree: v.isFree,
          isConcluded: v.isConcluded
        }))
      )
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)

      // Crea e attiva il download
      const a = document.createElement('a')
      a.href = url
      a.download = `${dayjs().format('YYYYMMDD')}_events_stats.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast({
        title: t('csv_success'),
        status: 'success',
        duration: 4000,
        isClosable: true
      })
    } catch (error) {
      log.error('Errore nella conversione JSON a CSV', error)
      toast({
        title: t('csv_error'),
        description: getErrorMessage(error),
        status: 'error',
        duration: 4000,
        isClosable: true
      })
    }
  }, [from, to, canUpdate, toast, t])

  return (
    <Flex bg={'greyNeutral100'} direction={'column'} padding={'40px 120px 20px 40px'} gap={'30px'} height={'100vh'} minH='100vh' overflow={'auto'}>
      <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left' marginTop={'30px'}>
        {t('stat')}
      </Text>
      <Grid templateColumns='repeat(2, 1fr)' gap={'20px'}>
        <HStack gap={5}>
          <Button variant={'refresh'} onClick={refreshData} isLoading={loading}>
            {t('refresh_data')}
          </Button>
          <Text fontSize='11px' fontWeight='500' lineHeight='14px' color={'greyCold300'}>
            {t('last_update')}:{`\n`}
            {lastUpdate}
          </Text>
          <Select
            placeholder='Select option'
            defaultValue={'last_year'}
            onChange={(e): void => setIntervalType(e.target.value as Interval)}
            backgroundColor={'white'}>
            {/*<option value='last_day'>{t('last_day')}</option>*/}
            <option value='last_week'>{t('last_week')}</option>
            <option value='last_month'>{t('last_month')}</option>
            <option value='last_year'>{t('last_year')}</option>
            <option value='custom'>{t('custom')}</option>
          </Select>
        </HStack>
        {intervalType !== 'custom' ? (
          <Spacer></Spacer>
        ) : (
          <HStack>
            <HStack borderColor={'greyCold100'} paddingRight={8} borderWidth={1} borderRadius={8} backgroundColor={'white'}>
              <SingleDatepicker name='date-input' date={dayjs(from).toDate()} onDateChange={fromDate} disabled={loading} />
              <Text fontSize='14px' fontWeight='500' lineHeight='16px' color={'greyCold300'}>
                {t('from')}: {dayjs(from).format(`DD/MM/YYYY`)}
              </Text>
            </HStack>
            <HStack borderColor={'greyCold100'} paddingRight={8} borderWidth={1} borderRadius={8} backgroundColor={'white'}>
              <SingleDatepicker name='date-input' date={dayjs(to).toDate()} onDateChange={toDate} disabled={loading} />
              <Text fontSize='14px' fontWeight='500' lineHeight='16px' color={'greyCold300'}>
                {t('to')}: {dayjs(to).format(`DD/MM/YYYY`)}
              </Text>
            </HStack>
          </HStack>
        )}
        {canUpdate && (
          <HStack gridColumn={'1 / span 2'} width={'100%'}>
            <Button variant='outline' key={'export_user_csv'} onClick={generateUsersCsv}>
              <i data-eva={'download-outline'} data-eva-fill={'#353F48'}></i>
              {t('graphs.export_user_csv')}
            </Button>
            <Button variant='outline' key={'export_payments_csv'} onClick={generatePaymentsCsv}>
              <i data-eva={'download-outline'} data-eva-fill={'#353F48'}></i>
              {t('graphs.export_payments_csv')}
            </Button>
            <Button variant='outline' key={'export_events_csv'} onClick={generateEventsCsv}>
              <i data-eva={'download-outline'} data-eva-fill={'#353F48'}></i>
              {t('graphs.export_events_csv')}
            </Button>
          </HStack>
        )}
        <Box
          as='iframe'
          src={`${process.env.REACT_APP_GRAPH_1}&from=${from}&to=${to}`}
          width='100%'
          height='250'
          style={{ borderRadius: '8px' }}
          frameBorder='0'></Box>
        <Box
          as='iframe'
          src={`${process.env.REACT_APP_GRAPH_2}&from=${from}&to=${to}`}
          width='100%'
          height='250'
          style={{ borderRadius: '8px' }}
          frameBorder='0'></Box>
        <Box
          as='iframe'
          src={`${process.env.REACT_APP_GRAPH_3}&from=${from}&to=${to}`}
          width='100%'
          height='350'
          style={{ borderRadius: '8px' }}
          frameBorder='0'></Box>
        <Box
          as='iframe'
          src={`${process.env.REACT_APP_GRAPH_4}&from=${from}&to=${to}`}
          width='100%'
          height='350'
          style={{ borderRadius: '8px' }}
          frameBorder='0'></Box>
        <Box
          as='iframe'
          src={`${process.env.REACT_APP_GRAPH_5}&from=${from}&to=${to}`}
          width='100%'
          height='350'
          style={{ borderRadius: '8px' }}
          frameBorder='0'></Box>
        <Box
          as='iframe'
          src={`${process.env.REACT_APP_GRAPH_6}&from=${from}&to=${to}`}
          width='100%'
          height='350'
          style={{ borderRadius: '8px' }}
          frameBorder='0'></Box>
      </Grid>
    </Flex>
  )
}

export { GraphDashboard }
